import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { ncractionApi, NCRAction, NCRActionSearchParams } from '../../services/ncractionApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  FETCH_NCRACTIONS,
  FETCH_NCRACTION_BY_ID,
  CREATE_NCRACTION,
  UPDATE_NCRACTION,
  DELETE_NCRACTION,
  SEARCH_NCRACTIONS,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/ncractionSlice';

// Worker Sagas
function* fetchNCRActionsSaga(action: PayloadAction<NCRActionSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const response = yield call(ncractionApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching NCR Actions:', error);
    yield put(setError(error.message || 'Failed to fetch NCR Actions'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchNCRActionByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const ncraction = yield call(ncractionApi.getById, action.payload);
    yield put(setSelectedItem(ncraction));
  } catch (error: any) {
    console.error('Error fetching NCR Action:', error);
    yield put(setError(error.message || 'Failed to fetch NCR Action'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createNCRActionSaga(action: PayloadAction<Omit<NCRAction, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncractionApi.validation.validateNCRAction, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newNCRAction = yield call(ncractionApi.create, action.payload);
    yield put(addItem(newNCRAction));
    yield put(setSuccessMessage('NCR Action created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating NCR Action:', error);
    yield put(setError(error.message || 'Failed to create NCR Action'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateNCRActionSaga(action: PayloadAction<{ id: number; ncraction: Partial<NCRAction> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, ncraction } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(ncractionApi.validation.validateNCRAction, ncraction);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedNCRAction = yield call(ncractionApi.update, id, ncraction);
    yield put(updateItem(updatedNCRAction));
    yield put(setSuccessMessage('NCR Action updated successfully'));
  } catch (error: any) {
    console.error('Error updating NCR Action:', error);
    yield put(setError(error.message || 'Failed to update NCR Action'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteNCRActionSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(ncractionApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('NCR Action deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete NCR Action'));
    }
  } catch (error: any) {
    console.error('Error deleting NCR Action:', error);
    yield put(setError(error.message || 'Failed to delete NCR Action'));
  } finally {
    yield put(setLoading(false));
  }
}

function* searchNCRActionsSaga(action: PayloadAction<NCRActionSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(ncractionApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching NCR Actions:', error);
    yield put(setError(error.message || 'Failed to search NCR Actions'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(ncractionApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncractionApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(ncractionApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully activated ${action.payload.length} NCR Actions`));
      // Refresh the list to show updated status
      yield call(fetchNCRActionsSaga, { payload: undefined, type: FETCH_NCRACTIONS });
    } else {
      yield put(setError(response.message || 'Failed to activate NCR Actions'));
    }
  } catch (error: any) {
    console.error('Error bulk activating NCR Actions:', error);
    yield put(setError(error.message || 'Failed to activate NCR Actions'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncractionApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(ncractionApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deactivated ${action.payload.length} NCR Actions`));
      // Refresh the list to show updated status
      yield call(fetchNCRActionsSaga, { payload: undefined, type: FETCH_NCRACTIONS });
    } else {
      yield put(setError(response.message || 'Failed to deactivate NCR Actions'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating NCR Actions:', error);
    yield put(setError(error.message || 'Failed to deactivate NCR Actions'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncractionApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(ncractionApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deleted ${action.payload.length} NCR Actions`));
      // Refresh the list to show updated items
      yield call(fetchNCRActionsSaga, { payload: undefined, type: FETCH_NCRACTIONS });
    } else {
      yield put(setError(response.message || 'Failed to delete NCR Actions'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting NCR Actions:', error);
    yield put(setError(error.message || 'Failed to delete NCR Actions'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* exportToCSVSaga(action: PayloadAction<NCRActionSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const blob = yield call(ncractionApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ncractions-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('NCR Actions exported successfully'));
  } catch (error: any) {
    console.error('Error exporting NCR Actions:', error);
    yield put(setError(error.message || 'Failed to export NCR Actions'));
  } finally {
    yield put(setLoading(false));
  }
}

// Watcher Sagas
function* watchFetchNCRActions(): Generator<any, void, any> {
  yield takeLatest(FETCH_NCRACTIONS, fetchNCRActionsSaga);
}

function* watchFetchNCRActionById(): Generator<any, void, any> {
  yield takeEvery(FETCH_NCRACTION_BY_ID, fetchNCRActionByIdSaga);
}

function* watchCreateNCRAction(): Generator<any, void, any> {
  yield takeEvery(CREATE_NCRACTION, createNCRActionSaga);
}

function* watchUpdateNCRAction(): Generator<any, void, any> {
  yield takeEvery(UPDATE_NCRACTION, updateNCRActionSaga);
}

function* watchDeleteNCRAction(): Generator<any, void, any> {
  yield takeEvery(DELETE_NCRACTION, deleteNCRActionSaga);
}

function* watchSearchNCRActions(): Generator<any, void, any> {
  yield takeLatest(SEARCH_NCRACTIONS, searchNCRActionsSaga);
}

function* watchFetchStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATISTICS, fetchStatisticsSaga);
}

function* watchBulkActivate(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
}

function* watchBulkDeactivate(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
}

function* watchBulkDelete(): Generator<any, void, any> {
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
}

function* watchExportToCSV(): Generator<any, void, any> {
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
}

// Root saga
export function* ncractionSaga(): Generator<any, void, any> {
  yield all([
    watchFetchNCRActions(),
    watchFetchNCRActionById(),
    watchCreateNCRAction(),
    watchUpdateNCRAction(),
    watchDeleteNCRAction(),
    watchSearchNCRActions(),
    watchFetchStatistics(),
    watchBulkActivate(),
    watchBulkDeactivate(),
    watchBulkDelete(),
    watchExportToCSV(),
  ]);
} 